import { Component, OnInit, OnDestroy, ViewChild, Input, EventEmitter } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { EupRoutesService } from '../../core/eupRoutes.service';
import { EupHttpHandler } from '../../core/eupHttpHandler.service';
import { RouterInterceptorService } from '../../core/routerInterceptor.service';
import { SettingsModal } from '../../settings/settings.component';
import { Utils } from '../utils.service';
import { GlobalSettingsService, ContextParams, GlobalSettings } from '../../core/globalSettings.service';
import { Company, Contact } from '../generalInterfaces';
import { GoogleAnalyticsService } from '../../core/googleAnalytics.service';
import { AccessibilityService } from '../accessibility.service';
import { MessagesService } from '../../messages/messages.service';
import { DownloadNotificationService } from '../downloadNotification/downloadNotification.service';
import { DownloadNotification } from '../downloadNotification/downloadNotification.component';
import { AuthService } from '../../core/auth.service';
import { LocalStorageService } from '@core/localStorage.service';
import { Consts } from '@shared/consts';

@Component({
	selector: 'eup-sticky-header',
	templateUrl: './stickyHeader.component.html',
	styleUrls: [ './stickyHeader.component.scss' ]
})
export class StickyHeaderComponent implements OnInit, OnDestroy {
	public static readonly homeImage = 'home46';
	public static readonly homeLink = 'home';
	private readonly backImage = 'backButton46';
	private rolePath: string;
	public messagesCount = 0;
	private storage: Storage;

	isLogoutOpen = false;
	isLinksMenuOpen = false;
	isDoctorsMenuOpen = false;
	isCompaniesMenuOpen = false;

	companies: Company[];
	doctors: Contact[];
	selectedCompany: Company;
	selectedDoctor: Contact;

	contextChangedSubscription: Subscription;
	getContactsSubscription: Subscription;
	homeLinkClickSubscription: Subscription;

	@Input() titleText = '';
	@Input() image = StickyHeaderComponent.homeImage;
	@Input() backLink = StickyHeaderComponent.homeLink;
	@Input() backToLastPage = false;
	@Input() disableContactSelection = false;
	@Input() hideContactSelection = false;
	@Input() onHomeLinkClickCallback: Function;
	@Input() homeLinkClickEvent: EventEmitter<any>;
	@Input() disableSiteNavigation = 'false';
	@Input() isBackLinkAbsoluteUrl = 'false';
	@Input() shouldAddRoleToBackLink = 'true';
	@ViewChild(SettingsModal) settingsModal: SettingsModal;
	@ViewChild(DownloadNotification) downloadNotification: DownloadNotification;

	constructor(
		private router: Router,
		private http: EupHttpHandler,
		public globalSettingsService: GlobalSettingsService,
		private utils: Utils,
		private routerInterceptor: RouterInterceptorService,
		private googleAnalyticsService: GoogleAnalyticsService,
		private eupRoutesService: EupRoutesService,
		private route: ActivatedRoute,
		private accessibilityService: AccessibilityService,
		private messagesService: MessagesService,
		private downloadNotificationService: DownloadNotificationService,
		private authService: AuthService,
		private localStorageService: LocalStorageService
	) {
		this.storage = localStorageService.storage;
	}

	get hasFullOrdersVisibility(): boolean {
		return this.settings.hasFullOrdersVisibility;
	}

	get username(): string {
		return this.settings.username;
	}

	get disableSiteNavigationFunc(): boolean {
		return this.disableSiteNavigation === 'true';
	}

	get isBackLinkAbsoluteUrlFunc(): boolean {
		return this.isBackLinkAbsoluteUrl === 'true';
	}

	get shouldAddRoleToBackLinkFunc(): boolean {
		return this.shouldAddRoleToBackLink === 'true';
	}

	get isCompanyDropdownDisabled(): boolean {
		return this.disableContactSelection || this.companies.length === 1;
	}

	get isDoctorDropdownDisabled(): boolean {
		return (
			!this.hasFullOrdersVisibility ||
			this.disableContactSelection ||
			this.doctors.length === 1 ||
			this.disableSiteNavigationFunc
		);
	}

	private get settings(): GlobalSettings {
		return this.globalSettingsService.get();
	}

	toggleLogoutMenu() {
		this.isLogoutOpen = !this.isLogoutOpen;
	}

	hideLogoutMenu() {
		this.isLogoutOpen = false;
	}

	toggleLinksMenu() {
		this.isLinksMenuOpen = !this.isLinksMenuOpen;
	}

	hideLinksMenu() {
		this.isLinksMenuOpen = false;
	}

	ngOnDestroy(): void {
		this.utils.clearObservablesSubscriptions(this);
	}

	hideDoctorMenu(): void {
		this.isDoctorsMenuOpen = false;
	}

	hideCompaniesMenu(): void {
		this.isCompaniesMenuOpen = false;
	}

	hideDoctorAndCompaniesMenu(): void {
		this.isDoctorsMenuOpen = false;
		this.isCompaniesMenuOpen = false;
	}

	companiesMenuToggled(isOpened: boolean): void {
		if (isOpened) {
			this.hideLinksMenu();
			this.hideLogoutMenu();
			this.downloadNotification.onClose();
		}

		this.isCompaniesMenuOpen = isOpened;
	}

	doctorsMenuToggled(isOpened: boolean): void {
		if (isOpened) {
			this.hideLinksMenu();
			this.hideLogoutMenu();
			this.downloadNotification.onClose();
		}

		this.isDoctorsMenuOpen = isOpened;
	}

	ngOnInit(): void {
		const selectedCompanyId = +this.settings.selectedCompanyId;
		this.companies = this.settings.companies;
		this.rolePath = this.globalSettingsService.rolePath();
		if (!this.isBackLinkAbsoluteUrlFunc && this.shouldAddRoleToBackLinkFunc) {
			this.backLink = this.rolePath + '/' + this.backLink;
		}
		this.selectedCompany = this.getCompanyById(selectedCompanyId);
		this.redirectIfRequired(this.selectedCompany);
		this.fillDoctors(selectedCompanyId);
		this.contextChangedSubscription = this.globalSettingsService.contextChanged.subscribe(
			(params: ContextParams) => {
				if (!params.companyId || !params.doctorId) {
					return;
				}
				this.onCompanyChanged(this.getCompanyById(+params.companyId));
				this.onDoctorChanged(this.getDoctorById(+params.doctorId));
			}
		);

		// when this property is set to true - override image and back link
		// according to previous url
		if (this.backToLastPage) {
			const previousRoute = this.routerInterceptor.previousRoute;
			// if no previous url exists (e.g. user entered url in a new tab)
			// or the previous url is home so back button should link to home page
			// or user was redirected to a page from straight from login
			if (
				!previousRoute ||
				previousRoute === '/' + this.rolePath + '/' + StickyHeaderComponent.homeLink ||
				previousRoute === '/login' ||
				previousRoute === '/'
			) {
				this.backLink = this.rolePath + '/' + StickyHeaderComponent.homeLink;
				this.image = StickyHeaderComponent.homeImage;
			} else {
				// a previous url exists and it is not the home page so back button should link
				// to the last page that the user was at
				this.backLink = previousRoute.slice(1);
				this.image = this.backImage;
			}
		}

		this.messagesService.getMessagesCount().subscribe((res: number) => {
			this.messagesCount = res;
		});

		this.preselectCompany();
	}

	logout(): void {
		this.http.post(this.eupRoutesService.home.logout).subscribe(() => {
			this.downloadNotificationService.clear();
			this.authService.logout();
		});
	}

	onCompanyChanged(company: Company): void {
		if (this.selectedCompany === company) {
			return;
		}

		this.redirectIfRequired(company);

		this.selectedCompany = company;
		this.globalSettingsService.setCompanyAndDefaultDoctor(company.id);
		this.globalSettingsService.setCompanySoftwareOptions(company.id);
		
		this.fillDoctors(company.id);

		this.googleAnalyticsService.hitEvent('Home', 'Change Company');
	}

	onDoctorChanged(doctor: Contact): void {
		if (this.selectedDoctor === doctor || doctor === undefined) {
			return;
		}

		this.selectedDoctor = doctor;
		this.globalSettingsService.setDoctor(doctor.contactId);

		this.googleAnalyticsService.hitEvent('Home', 'Change Doctor');
	}

	onImageLinkClick(): void {
		if (this.onHomeLinkClickCallback && this.homeLinkClickEvent) {
			if (!this.homeLinkClickSubscription) {
				this.homeLinkClickSubscription = this.homeLinkClickEvent.subscribe((res: any) => {
					this.router.navigateByUrl(`/${this.backLink.toLowerCase()}`);
				});
			}
			this.onHomeLinkClickCallback();
		} else {
			if (this.isBackLinkAbsoluteUrlFunc) {
				window.location.href = this.backLink;
			} else {
				this.router.navigateByUrl(`/${this.backLink.toLowerCase()}`);
			}
		}
	}

	private getDoctorById(id: number): Contact {
		return this.doctors.filter((c: Contact) => c.contactId === id)[0];
	}

	private getCompanyById(id: number): Company {
		return this.settings.companies.filter((c: Company) => c.id === id)[0];
	}

	private fillDoctors(companyId: number): void {
		this.doctors = [ { contactId: this.settings.contactId, contactName: this.settings.username } ];

		this.selectedDoctor = this.doctors[0];

		if (this.hasFullOrdersVisibility) {
			this.getContactsSubscription = this.globalSettingsService
				.getContactsForCompany(companyId)
				.subscribe((data: Contact[]) => {
					this.doctors = data;
					if (
						this.doctors.filter((c: Contact) => c.contactId === +this.settings.selectedDoctorId).length > 0
					) {
						this.selectedDoctor = this.getDoctorById(+this.settings.selectedDoctorId);
					}
				});
		}
	}

	private preselectCompany() {
		this.route.queryParams.subscribe((params) => {
			if (!params['companyid']) {
				return;
			}
			this.onCompanyChanged(this.getCompanyById(+params['companyid']));
		});
	}

	private redirectIfRequired(company: Company): void {
		if (!company.handlingEupUrl) {
			return;
		}
		if (company.handlingEupUrl.endsWith('/')) {
			company.handlingEupUrl = company.handlingEupUrl.slice(0, -1);
		}

		const myIteroBffUrl = new URL(this.eupRoutesService.serverUrl);
		const companyMyIteroBffUrl = new URL(company.handlingEupUrl);
		const currentPath = this.accessibilityService.getPathName();
		const myiteroComUrl = new URL(
			`${this.accessibilityService.getLocation().origin}/login?returnUrl=${currentPath}&companyId=${company.id}`
		);

		const allowRedirection = myIteroBffUrl.host !== companyMyIteroBffUrl.host;
		if (allowRedirection) {
			this.http.post(this.eupRoutesService.home.logout).subscribe(() => {
				this.downloadNotificationService.clear();
				this.globalSettingsService.clear();
				this.storage[Consts.Storage.RegionalApiEndpoint] = `${companyMyIteroBffUrl.origin}/`;
				this.accessibilityService.redirectTo(myiteroComUrl.href);
			});
			return;
		}
	}
}
